import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, TextField, Paper } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { updateBscGoal } from "../../../api/companyApi";
import { useNotification } from "../../../providers/NotificationProvider";
import DetailField from "../../../components/common/DetailField";
import { BSC_CATEGORY_CONFIG } from "./bscConfig";

const toDateInput = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10);
};

export default function BscGoalEditPage() {
  const { businessDomainId, businessUnitId, category, goalIdx } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/bsc/${category}`;

  const config = BSC_CATEGORY_CONFIG[category];

  const [originalGoal, setOriginalGoal] = useState(null);
  const [form, setForm] = useState({ plannedValue: "", plannedDate: "", deadline: "" });

  const loadData = useCallback(async () => {
    if (!config) return;
    try {
      const data = await config.fetchGoals(businessUnitId);
      const item = Array.isArray(data) ? data[Number(goalIdx)] : null;
      if (item?.goal) {
        setOriginalGoal(item.goal);
        setForm({
          plannedValue: item.goal.plannedValue || "",
          plannedDate: toDateInput(item.goal.plannedDate),
          deadline: toDateInput(item.goal.deadline),
        });
      }
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId, category, goalIdx]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const updatedGoal = {
      ...originalGoal,
      plannedValue: form.plannedValue,
      plannedDate: form.plannedDate ? `${form.plannedDate}T00:00:00.000Z` : null,
      deadline: form.deadline ? `${form.deadline}T00:00:00.000Z` : null,
      updatedGoalAt: new Date().toISOString(),
    };
    try {
      await updateBscGoal(businessUnitId, updatedGoal);
      notify.success(t("message.update.success"));
      navigate(basePath);
    } catch (err) { notify.apiError(err); }
  };

  if (!config) {
    return <Typography color="error">Unknown BSC category: {category}</Typography>;
  }
  if (!originalGoal) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t(config.titleKey)} - {t("bsc.goal.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <DetailField
              label={t("bsc.indicator")}
              value={`${originalGoal.indicator?.indicatorName || ""}${originalGoal.indicator?.unit ? `（${originalGoal.indicator.unit}）` : ""}`}
            />
            {originalGoal.indicator && (
              <Paper variant="outlined" sx={{ mt: 1, p: 2, bgcolor: "grey.50" }}>
                {[
                  { label: t("appKpi.unit"),              value: originalGoal.indicator.unit },
                  { label: t("appKpi.measuringMethod"),    value: originalGoal.indicator.measuringMethod },
                  { label: t("appKpi.evaluationStandard"), value: originalGoal.indicator.evaluationStandard },
                  { label: t("appKpi.description"),        value: originalGoal.indicator.description },
                ].filter((r) => r.value).map((r) => (
                  <Box key={r.label} sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>{r.label}:</Typography>
                    <Typography variant="body2">{r.value}</Typography>
                  </Box>
                ))}
              </Paper>
            )}
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              required
              label={`${t("bsc.plannedValue")}${originalGoal.indicator?.unit ? `（${originalGoal.indicator.unit}）` : ""}`}
              name="plannedValue"
              value={form.plannedValue}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              type="date"
              label={t("bsc.plannedDate")}
              name="plannedDate"
              value={form.plannedDate}
              onChange={handleChange}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              type="date"
              label={t("bsc.deadline")}
              name="deadline"
              value={form.deadline}
              onChange={handleChange}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(basePath)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
